import {
  AssignmentLate,
  ChevronRight,
  ContentPasteOutlined,
  ExpandLess,
  ExpandMore,
  HelpOutline,
  Payment,
  Receipt,
  Warning,
} from "@mui/icons-material";
import StarIcon from "@mui/icons-material/Star";
import {
  AppBar,
  Box,
  Button,
  Collapse,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { createContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import useFetch from "src/Components/Common/useFetch";
import CustomToast from "src/Components/CustomToast";
import nakadlogo from "src/Graphics/icon.png";
import nakadImg from "src/Graphics/NAKAD_FINAL (1).png";
import accuteImg from "src/Graphics/VendorPortal/accute.png";
import instamartSmallImg from "src/Graphics/VendorPortal/instamartLogo.png";
import instamartMainImg from "src/Graphics/VendorPortal/instamartMain.png";
import rupeeVectorImg from "src/Graphics/VendorPortal/ruppeVector.png";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
// import { ENV_NAME } from "src/Utils/Recon/ENV/Constants";
import { extractVendorPortalIdentityFromToken, identifyVendorPortalUser } from "src/Utils/Mixpanel/mixpanel";
import { setVendorPortalSuperProps, trackSessionStarted } from "src/Utils/Mixpanel/VendorPortalEvents";
import { uiLoggerNamesVendorPortal } from "src/Utils/Recon/UiLogger/Constants";
import AlertsComponent from "./AlertsComponent";

interface VendorPortalLayoutProps {
  children: React.ReactNode;
  embedMode?: boolean;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  hasSubmenu: boolean;
  subItems?: SubMenuItem[];
}

interface SubMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

// Create the context
interface VendorPortalContextType {
  handleUnauthorizedError: (error: any) => void;
  uiLogsVendorPortal: (message: string, logDetails: any) => void;
  isSessionReady: boolean;
  token: string | null;
  userPan: string | null;
  bannerData: {
    pan: string;
    vendorName: string;
    rate: string;
    text: string;
    creditPeriod: string;
    hasFilledForm: boolean;
    createdAt: string;
    updatedAt: string;
  };
  setBannerData: (data: {
    pan: string;
    vendorName: string;
    rate: string;
    text: string;
    creditPeriod: string;
    hasFilledForm: boolean;
    createdAt: string;
    updatedAt: string;
  }) => void;
}

export const VendorPortalContext = createContext<VendorPortalContextType | undefined>(undefined);
// Custom hook to use the context
export const useVendorPortal = () => {
  const context = useContext(VendorPortalContext);
  if (context === undefined) {
    throw new Error("useVendorPortal must be used within a VendorPortalProvider");
  }
  return context;
};
const VendorPortalLayout: React.FC<VendorPortalLayoutProps> = ({ children, embedMode = false }) => {
  const history = useHistory();
  const location = useLocation();
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [userPan, setUserPan] = useState<string | null>(null);
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [bannerData, setBannerData] = useState(null); // State to store banner data

  // Get active menu item from current path
  const getActiveMenuFromPath = (pathname: string) => {
    if (pathname.includes("/ledger-summary")) return "Ledger Summary";
    if (pathname.includes("/hold-resolution")) return "Hold Resolution";
    if (pathname.includes("/invoice-rejection")) return "Invoice Rejection";
    if (pathname.includes("/reconciliation")) return "Reconciliation Summary";
    if (pathname.includes("/disputed-entries")) return "Recon Dispute Entries";
    if (pathname.includes("/raise-other-issues")) return "Raise Other Issues";

    if (pathname.includes("/early-payment")) return "Early Payment";
    if (pathname.includes("/faq")) return "FAQ";
    // if (pathname.includes("/optonvp")) return "optonvp";
    // if (pathname.includes("/closevp")) return "closevp";
    return "Ledger Summary"; // Default vendor portal menu item
  };

  const [activeMenuItem, setActiveMenuItem] = useState(() => getActiveMenuFromPath(location.pathname));

  // Update active menu item when location changes
  useEffect(() => {
    const newActiveItem = getActiveMenuFromPath(location.pathname);
    setActiveMenuItem(newActiveItem);
  }, [location.pathname]);

  const drawerWidth = 274;
  const collapsedDrawerWidth = 64;

  // Navigation menu items
  const baseMenuItems: MenuItem[] = [
    {
      id: "ledger-summary",
      label: "Ledger Summary",
      icon: <ContentPasteOutlined />,
      path: "/vendorPortal/ledger-summary",
      hasSubmenu: false,
    },
    {
      id: "hold-resolution",
      label: "Hold Resolution",
      icon: <AssignmentLate />,
      path: "/vendorPortal/hold-resolution",
      hasSubmenu: false,
    },
    {
      id: "invoice-rejection",
      label: "Invoice Rejection",
      icon: <Warning />,
      path: "/vendorPortal/invoice-rejection",
      hasSubmenu: false,
    },
    {
      id: "reconciliation",
      label: "Reconciliation Summary",
      icon: <StarIcon />,
      path: "/vendorPortal/reconciliation",
      hasSubmenu: false,
    },
    {
      id: "disputed-entries",
      label: "Recon Dispute Entries",
      icon: <Receipt />,
      path: "/vendorPortal/disputed-entries",
      hasSubmenu: false,
    },
    {
      id: "raise-other-issues",
      label: "Raise Other Issues",
      icon: <AssignmentLate />,
      path: "/vendorPortal/raise-other-issues",
      hasSubmenu: false,
    },

    {
      id: "early-payment",
      label: "Early Payment",
      icon: <Payment />,
      path: "/vendorPortal/early-payment",
      hasSubmenu: false,
    },
    {
      id: "faq",
      label: "FAQ",
      icon: <HelpOutline />,
      path: "/vendorPortal/faq",
      hasSubmenu: false,
    },
    // {
    //   id: "future-vp-subtab",
    //   label: "Future VP SubTab",
    //   icon: <Folder />,
    //   hasSubmenu: true,
    //   subItems: [
    //     {
    //       id: "optonvp",
    //       label: "optonvp",
    //       icon: <FolderOpen />,
    //       path: "/vendorPortal/optonvp",
    //     },
    //     {
    //       id: "closevp",
    //       label: "closevp",
    //       icon: <FolderOpen />,
    //       path: "/vendorPortal/closevp",
    //     },
    //   ],
    // },
  ];

  // Filter menu items based on PAN
  // const specialPANs = [
  //   "ABACS8927L",
  //   "AAFCH0247Q",
  //   "AAACK1748A",
  //   "AAXCS2385L",
  //   "AAACD0474C",
  //   "AABCR2648M",
  //   "AAACM7493G",
  //   "AAACH7252A",
  //   "AAOCA0655N",
  //   "AFWPA6217L",
  //   "AFCFS3573F",
  //   "AAICV4484C",
  //   "AAAAT0459K",
  //   "AAACM1635J",
  //   "AABCM6771A",
  //   "AAACW5582G",
  //   "AAACZ7446G",
  //   "AADFT3025B",
  //   "AABCZ1623Q",
  //   "AALAS8422L",
  //   "AAACV2678L",
  //   "AACCT0588K",
  //   "AAGCD6753N",
  //   "AAJCK2567N",
  //   "AALCA0537G",
  //   "AAACH3005M",
  //   "AAJFF1795J",
  //   "AADHK7748E",
  //   "AAACA4220B",
  //   "AIRPS8391K",
  //   "AAECD1387J",
  //   "AAGCD6653F",
  //   "CPMPK9406B",
  //   "AALCT7748L",
  //   "AAOCA2678P",
  //   "AAHCS6664E",
  //   "AAFCH3843L",
  //   "AAXCS8295B",
  //   "AABCL5987H",
  //   "AAGCK6893E",
  //   "AABCD1683Q",
  //   "AZUPS2373R",
  //   "AAICC9694H",
  //   "ABGCS4796R",
  //   "AAACP0485D",
  //   "AAFCT7255E",
  //   "AAPFC2096K",
  //   "AAACX1645B",
  //   "AADCC3647H",
  //   "AAFCI1834E",
  //   "AAPCM3737Q",
  //   "AABCU8760D",
  //   "AAAAB2402J",
  //   "AADCE8061F",
  //   "AAAHR0578J",
  //   "ABLFR1523R",
  //   "ABCFB4834C",
  //   "AAACF2160M",
  //   "AAECH4091H",
  //   "EEDPS6808K",
  //   "AAOCM4305K",
  //   "AAICT4846L",
  //   "AAVCS4669R",
  //   "AUFPK5097C",
  //   "AACCJ6084E",
  //   "AAFCK3763N",
  //   "AAICA4264B",
  //   "AAJCP5565B",
  //   "AEVFS1056H",
  //   "AAHCT0925B",
  //   "AAMCR0470N",
  //   "AAPFA9880K",
  //   "AAJCG2944B",
  //   "AAACG2038F",
  //   "AAKCC4411A",
  //   "DACPS6302A",
  //   "ACTFS1922J",
  //   "ABLCS5147H",
  //   "AABCD7572N",
  //   "AAACH7412G",
  //   "AAICP2804J",
  //   "AADCH3292H",
  //   "AAACI1048K",
  //   "AAHCC0850A",
  //   "AAECD4299F",
  //   "BHAPS7681D",
  //   "AAACB3754B",
  //   "AACCV5763A",
  //   "AAACZ9479M",
  //   "ACGFS4283D",
  //   "AARFG6393F",
  //   "AAAFJ0286B",
  //   "AAGCG6502L",
  //   "AAACA2423J",
  //   "ABGCS8246N",
  //   "AAZCS3788B",
  //   "AAFCH6096P",
  //   "AAECF5520H",
  //   "AIVPG1947P",
  //   "AAAFA0338B",
  //   "AAACB6833D",
  //   "AADCN5660R",
  //   "AADCI3821M",
  //   "AAPCP6918A",
  //   "ASZPP8529N",
  //   "AACCP1881D",
  //   "ABFCS9450D",
  //   "AAGCB1395N",
  //   "AALCA1038J",
  //   "AAACK4644H",
  //   "AAGCD8374B",
  //   "AAFCC2721A",
  //   "AABCL7705P",
  //   "AAECJ5469K",
  //   "AADCK0743L",
  //   "APGPM1516G",
  //   "AACCA4437G",
  //   "AAECM6253C",
  //   "AADCH6305B",
  //   "AAICR2252C",
  //   "AAHCK9880C",
  //   "AADCB6432B",
  //   "AAICD3759J",
  //   "AUHPP5460C",
  //   "AABCN4641A",
  //   "AAACV6543N",
  //   "AAYCS0313H",
  //   "AAACO0471G",
  //   "AABCF8750G",
  //   "AANFG1668D",
  //   "AGTPA2785K",
  //   "AAJCM8241M",
  //   "AABCD9300J",
  //   "AAGFT9023N",
  //   "AAACP4156B",
  //   "ABNFM9859M",
  //   "BRYPT8537K",
  //   "AHMPG6908J",
  //   "AAJCN6837A",
  //   "AWTPM0958J",
  //   "AAICN5450A",
  //   "AADCL1356J",
  //   "AAGCH6000C",
  //   "ADKFS1534Q",
  //   "AASCM0767B",
  //   "AAICC4845L",
  //   "AAGCB4068A",
  //   "AABCZ7466M",
  //   "AAACC1647E",
  //   "AAECA0002D",
  //   "AAICR9588D",
  //   "AAACK4647E",
  //   "AAHCM7396M",
  //   "ADNPH9990A",
  // ];
  // const isSpecialUser = userPan && specialPANs.includes(userPan);
  // const isTestEnvironment = import.meta.env.VITE_APP_NAKAD_ENV !== ENV_NAME.production;

  const menuItems = baseMenuItems.filter((item) => {
    // FAQ tab is always visible
    if (item.id === "faq") return true;
    // Early Payment tab only visible if bannerData is present
    if (item.id === "early-payment") {
      return !!bannerData;
    }
    // if (isTestEnvironment || isSpecialUser) {
    //   // Test environment or special PANs see all menu items
    //   return true;
    // } else {
    //   // Others see only invoice-rejection and reconciliation
    //   return item.id === "invoice-rejection" || item.id === "reconciliation";
    // }
    return true;
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const apiKey = urlParams.get("apiKey");
    const emailId = urlParams.get("emailId");
    const urlToken = urlParams.get("token");
    if (apiKey && emailId) {
      createSessionForVendorPortal(apiKey, emailId);
    } else if (urlToken) {
      // Token passed directly via URL (e.g. from external platform embed)
      localStorage.setItem("vendorPortalToken", urlToken);
      fetchBannerData(urlToken);
      setToken(urlToken);
      setIsSessionReady(true);

      // Decode JWT to extract identity — token is the single source of truth
      const { email: tokenEmail, pan: tokenPan } = extractVendorPortalIdentityFromToken(urlToken);
      identifyVendorPortalUser(tokenEmail, tokenPan);
      setVendorPortalSuperProps({
        vendor_pan: tokenPan?.toUpperCase() || "N/A",
        brand_name: "Instamart",
        environment: import.meta.env.VITE_APP_NAKAD_ENV || "production",
        portal: "vendor_portal",
        theme: "default",
      });
      trackSessionStarted();
    } else {
      // Check for existing token
      const existingToken = localStorage.getItem("vendorPortalToken");
      const existingPAN = localStorage.getItem("vendorPortalPAN");
      if (existingToken) {
        fetchBannerData(existingToken); // Fetch banner data if token exists
        setToken(existingToken);
        setUserPan(existingPAN);
        setIsSessionReady(true);

        // Decode JWT to re-identify — token is the single source of truth
        const { email: tokenEmail, pan: tokenPan } = extractVendorPortalIdentityFromToken(existingToken);
        const resolvedPan = tokenPan ?? existingPAN;
        identifyVendorPortalUser(tokenEmail, resolvedPan);
        setVendorPortalSuperProps({
          vendor_pan: resolvedPan?.toUpperCase() || "N/A",
          brand_name: "Instamart",
          environment: import.meta.env.VITE_APP_NAKAD_ENV || "production",
          portal: "vendor_portal",
          theme: "default",
        });
      } else {
        toast.error(<CustomToast message="Session expired. Please log in again." />);
      }
    }
  }, []);

  const createSessionForVendorPortal = async (apiKey, emailId) => {
    await useFetch(API_ENDPOINTS.CREATE_VENDOR_PORTAL_SESSION.url, "GET", {
      failureMessage: API_ENDPOINTS.CREATE_VENDOR_PORTAL_SESSION.failureMessage,
      config: {
        params: {
          apiKey,
          emailId,
        },
      },
      thenCallBack: (response) => {
        const newToken = response.data.token;
        const pan = response.data.pan || null; // Try both lowercase and uppercase
        fetchBannerData(newToken); // Fetch banner data after getting the token

        if (newToken) {
          localStorage.setItem("vendorPortalToken", newToken);
          localStorage.setItem("vendorPortalPAN", pan || ""); // Store PAN in localStorage
          setToken(newToken);
          setUserPan(pan);
          setIsSessionReady(true);

          // Always decode the JWT to extract identity — token is the single source of truth
          const { email: tokenEmail, pan: tokenPan } = extractVendorPortalIdentityFromToken(newToken);
          const resolvedPan = tokenPan ?? pan;
          identifyVendorPortalUser(tokenEmail, resolvedPan);
          setVendorPortalSuperProps({
            vendor_pan: resolvedPan?.toUpperCase() || "N/A",
            brand_name: "Instamart",
            environment: import.meta.env.VITE_APP_NAKAD_ENV || "production",
            portal: "vendor_portal",
            theme: "default",
          });
          trackSessionStarted();

          // In embed mode, stay on the current embed route — don't redirect to regular vendor portal
          if (!embedMode) {
            history.push("/vendorPortal/ledger-summary");
          }
        }
      },
    });
  };

  const handleUnauthorizedError = (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem("vendorPortalToken");
      localStorage.removeItem("vendorPortalPAN");

      // In embed mode, silently re-create the session using URL params — never show login
      if (embedMode) {
        const urlParams = new URLSearchParams(window.location.search);
        const apiKey = urlParams.get("apiKey");
        const emailId = urlParams.get("emailId");
        if (apiKey && emailId) {
          createSessionForVendorPortal(apiKey, emailId);
          return;
        }
      }

      toast.error(<CustomToast message="Session expired. Please log in again." />);
    }
  };

  const menuNavLogMap: Record<string, { message: string; functionName: string }> = {
    "Ledger Summary": uiLoggerNamesVendorPortal.UI_SST_NAV_LEDGER_SUMMARY_CLICK,
    "Hold Resolution": uiLoggerNamesVendorPortal.UI_SST_NAV_LEDGER_SUMMARY_CLICK,
    "Invoice Rejection": uiLoggerNamesVendorPortal.UI_SST_NAV_INVOICE_REJECTION_CLICK,
    "Reconciliation Summary": uiLoggerNamesVendorPortal.UI_SST_NAV_RECONCILIATION_SUMMARY_CLICK,
    "Recon Dispute Entries": uiLoggerNamesVendorPortal.UI_SST_NAV_RECON_DISPUTE_ENTRIES_CLICK,
    "Raise Other Issues": uiLoggerNamesVendorPortal.UI_SST_NAV_RAISE_OTHER_ISSUES_CLICK,
    "Early Payment": uiLoggerNamesVendorPortal.UI_SST_NAV_EARLY_PAYMENT_CLICK,
    FAQ: uiLoggerNamesVendorPortal.UI_SST_NAV_FAQ_CLICK,
  };

  const handleMenuItemClick = (menuItem: string, path?: string) => {
    const logEntry = menuNavLogMap[menuItem];
    if (logEntry) {
      uiLogsVendorPortal(logEntry.message, { function: logEntry.functionName });
    }
    setActiveMenuItem(menuItem);
    if (path) {
      history.push(path);
    }
  };

  const handleSubmenuToggle = (menuId: string) => {
    setExpandedMenus((prev) => (prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId]));
  };

  const uiLogsVendorPortal = (message, logDetails) => {
    const token = localStorage.getItem("vendorPortalToken");
    useFetch(API_ENDPOINTS.LOG_UI_EVENTS_VENDOR_PORTAL.url, "POST", {
      failureMessage: API_ENDPOINTS.LOG_UI_EVENTS_VENDOR_PORTAL.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      data: {
        message,
        logDetails,
      },
    });
  };

  const fetchBannerData = (token) => {
    useFetch(API_ENDPOINTS.VENDOR_PORTAL_BANNER_DATA.url, "GET", {
      failureMessage: API_ENDPOINTS.VENDOR_PORTAL_BANNER_DATA.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      thenCallBack: (response) => {
        setBannerData(response.data.banner);
      },
      catchCallBack: (error) => {
        handleUnauthorizedError(error);
      },
    });
  };

  // Context value
  const vendorPortalContextValue: VendorPortalContextType = {
    handleUnauthorizedError,
    uiLogsVendorPortal,
    isSessionReady,
    token,
    userPan,
    bannerData, // Add banner data to context
    setBannerData, // Pass setBannerData to the context
  };

  return (
    <VendorPortalContext.Provider value={vendorPortalContextValue}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* App Bar */}
        {!embedMode && (
          <AppBar
            position="fixed"
            sx={{
              width: `calc(100% - ${isDrawerExpanded ? drawerWidth : collapsedDrawerWidth}px)`,
              ml: `${isDrawerExpanded ? drawerWidth : collapsedDrawerWidth}px`,
              backgroundColor: "#ffffff",
              color: "rgba(0,0,0,0.87)",
              boxShadow: "0px 1px 3px 0px rgba(0,0,0,0.12)",
              transition: "width 0.3s ease, margin-left 0.3s ease",
            }}
          >
            <Toolbar>
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 500 }}>
                {activeMenuItem}
              </Typography>

              {/* Right side controls */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="overline" sx={{ color: "rgba(0,0,0,0.6)", lineHeight: 1 }}>
                  Powered By
                </Typography>
                <img src={nakadImg} alt="Nakad Logo" style={{ height: 32 }} />
              </Box>
            </Toolbar>
          </AppBar>
        )}

        {/* Sidebar Drawer */}
        {!embedMode && (
          <Drawer
            sx={{
              width: isDrawerExpanded ? drawerWidth : collapsedDrawerWidth,
              flexShrink: 0,
              transition: "width 0.3s ease",
              "& .MuiDrawer-paper": {
                width: isDrawerExpanded ? drawerWidth : collapsedDrawerWidth,
                boxSizing: "border-box",
                backgroundColor: "#ffffff !important",
                boxShadow:
                  "0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)",
                transition: "width 0.3s ease",
                overflow: "hidden",
              },
            }}
            variant="permanent"
            anchor="left"
            onMouseEnter={() => setIsDrawerExpanded(true)}
            onMouseLeave={() => setIsDrawerExpanded(false)}
          >
            {/* Logo Section */}
            <Box sx={{ p: 2, backgroundColor: "#FAFAFA", transition: "padding 0.3s ease" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                {isDrawerExpanded ? (
                  <Box
                    sx={{
                      // width: 162,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <img
                      src={instamartMainImg}
                      alt="Instamart"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      // width: 162,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Box
                      component="img"
                      src={instamartSmallImg}
                      alt="Instamart"
                      sx={{
                        height: "17px !important",
                        width: "43px !important",
                        maxWidth: "none !important", // This prevents the 100% maxWidth inheritance
                        objectFit: "none", // This prevents any automatic scaling
                        flexShrink: 0, // This prevents the image from shrinking
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Navigation Menu */}
            <List sx={{ flex: 1, py: 1, backgroundColor: "#FAFAFA" }}>
              {menuItems.map((item) => (
                <React.Fragment key={item.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() =>
                        item.hasSubmenu ? handleSubmenuToggle(item.id) : handleMenuItemClick(item.label, item.path)
                      }
                      selected={activeMenuItem === item.label}
                      sx={{
                        mx: isDrawerExpanded ? 1 : 0.5,
                        borderRadius: 1,
                        transition: "all 0.2s ease-in-out, margin 0.3s ease",
                        justifyContent: isDrawerExpanded ? "flex-start" : "center",
                        px: isDrawerExpanded ? 2 : 1,
                        minHeight: 48,
                        "&.Mui-selected": {
                          backgroundColor: "rgba(84, 28, 76, 0.1)",
                          color: "#541c4c",
                          "&:hover": {
                            backgroundColor: "rgba(84, 28, 76, 0.15)",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                          transform: isDrawerExpanded ? "translateX(4px)" : "none",
                        },
                        "&:active": {
                          transform: isDrawerExpanded ? "translateX(2px)" : "none",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: activeMenuItem === item.label ? "#541c4c" : "rgba(0,0,0,0.54)",
                          minWidth: isDrawerExpanded ? 40 : "auto",
                          justifyContent: "center",
                          transition: "min-width 0.3s ease",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {isDrawerExpanded && (
                        <ListItemText
                          primary={item.label}
                          sx={{
                            opacity: isDrawerExpanded ? 1 : 0,
                            transition: "opacity 0.3s ease",
                            "& .MuiListItemText-primary": {
                              fontWeight: activeMenuItem === item.label ? 600 : 400,
                              color: activeMenuItem === item.label ? "#541c4c" : "rgba(0,0,0,0.87)",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      )}
                      {item.hasSubmenu &&
                        isDrawerExpanded &&
                        (expandedMenus.includes(item.id) ? <ExpandLess /> : <ExpandMore />)}
                      {activeMenuItem === item.label && isDrawerExpanded && !item.hasSubmenu && (
                        <Box
                          sx={{
                            width: 3,
                            height: 20,
                            backgroundColor: "#541c4c",
                            borderRadius: 1.5,
                            opacity: isDrawerExpanded ? 1 : 0,
                            transition: "opacity 0.3s ease",
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>

                  {/* Submenu Items */}
                  {item.hasSubmenu && item.subItems && expandedMenus.includes(item.id) && isDrawerExpanded && (
                    <Collapse in={expandedMenus.includes(item.id)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.subItems.map((subItem) => (
                          <ListItem key={subItem.id} disablePadding>
                            <ListItemButton
                              onClick={() => handleMenuItemClick(subItem.label, subItem.path)}
                              selected={activeMenuItem === subItem.label}
                              sx={{
                                pl: 4,
                                mx: 1,
                                borderRadius: 1,
                                transition: "all 0.2s ease-in-out",
                                minHeight: 40,
                                "&.Mui-selected": {
                                  backgroundColor: "rgba(84, 28, 76, 0.1)",
                                  color: "#541c4c",
                                  "&:hover": {
                                    backgroundColor: "rgba(84, 28, 76, 0.15)",
                                  },
                                },
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                                  transform: "translateX(4px)",
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  color: activeMenuItem === subItem.label ? "#541c4c" : "rgba(0,0,0,0.54)",
                                  minWidth: 40,
                                }}
                              >
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.label}
                                sx={{
                                  "& .MuiListItemText-primary": {
                                    fontWeight: activeMenuItem === subItem.label ? 600 : 400,
                                    color: activeMenuItem === subItem.label ? "#541c4c" : "rgba(0,0,0,0.87)",
                                    fontSize: "0.875rem",
                                  },
                                }}
                              />
                              {activeMenuItem === subItem.label && (
                                <Box
                                  sx={{
                                    width: 3,
                                    height: 15,
                                    backgroundColor: "#541c4c",
                                    borderRadius: 1.5,
                                  }}
                                />
                              )}
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              ))}
            </List>

            {/* Bottom Logo */}
            <Box sx={{ p: 2, backgroundColor: "#FAFAFA", transition: "padding 0.3s ease" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                {isDrawerExpanded ? (
                  <Box
                    component="a" // Change Box to be an anchor
                    href="https://www.nakad.co/"
                    target="_blank" // Open in new tab
                    rel="noopener noreferrer" // Security best practice for external links
                    sx={{
                      // width: 162,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <img
                      src={nakadImg}
                      alt="NAKAD"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        cursor: "pointer",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      // width: 162,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <img
                      src={nakadlogo}
                      alt="NAKAD"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Drawer>
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: "#f9f9f9",
            minHeight: "100vh",
            width: embedMode ? "100%" : `calc(100% - ${isDrawerExpanded ? drawerWidth : collapsedDrawerWidth}px)`,
            transition: "margin-left 0.3s ease, width 0.3s ease",
          }}
        >
          {!embedMode && <Toolbar />} {/* Space for fixed AppBar, skip in embed mode */}
          {/* Early Payment Banner */}
          {!embedMode &&
            bannerData &&
            !bannerData.hasFilledForm &&
            !location.pathname.includes("/early-payment") &&
            !location.pathname.includes("/faq") && (
              <Box sx={{ px: 2, pt: 4, pb: 2 }}>
                <Box sx={{ position: "relative", height: 113 }}>
                  {/* LIMITED TIME OFFER badge — floats above the card */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: 24,
                      zIndex: 2,
                      bgcolor: "#f9b200",
                      px: 1,
                      py: 0.5,
                      borderRadius: "5px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        color: "#000",
                        letterSpacing: "0.6px",
                        textTransform: "uppercase",
                        lineHeight: 1.3,
                      }}
                    >
                      Limited Time Offer
                    </Typography>
                  </Box>

                  {/* Main banner card */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to right, #0d47a1, #051a3b)",
                      border: "2px solid #ffb300",
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow:
                        "0px 1px 8px 0px rgba(0,0,0,0.12), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 3px 3px -2px rgba(0,0,0,0.20)",
                    }}
                  >
                    {/* Watermark rupee pattern */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -155,
                        right: -74,
                        width: 423,
                        height: 366,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transform: "rotate(-22.49deg)",
                        pointerEvents: "none",
                      }}
                    >
                      <img src={rupeeVectorImg} alt="" style={{ width: 355, height: 249, display: "block" }} />
                    </Box>

                    {/* Content row */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 30,
                        top: 22,
                        right: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* Left: text */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              borderBottom: "4px solid #f9b200",
                              pb: 0.5,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontStyle: "italic",
                                fontSize: 24,
                                color: "#f9b200",
                                lineHeight: 1.3,
                              }}
                            >
                              Get paid early
                            </Typography>
                          </Box>
                          <img src={accuteImg} alt="" style={{ width: 32, height: 32, flexShrink: 0 }} />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 20,
                            color: "white",
                            opacity: 0.8,
                            lineHeight: 1.3,
                          }}
                        >
                          on all your approved invoices
                        </Typography>
                      </Box>

                      {/* Right: buttons */}
                      <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", flexShrink: 0 }}>
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: "white",
                            color: "#000",
                            "&:hover": { bgcolor: "#f0f0f0" },
                            fontWeight: 600,
                            fontSize: 14,
                            letterSpacing: "0.014px",
                            px: 2,
                            py: 0.5,
                            minHeight: 40,
                            minWidth: 78,
                            borderRadius: "4px",
                            boxShadow: "none",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => {
                            uiLogsVendorPortal(
                              uiLoggerNamesVendorPortal.UI_SST_EP_BANNER_REQUEST_CALLBACK_CLICK.message,
                              {
                                function:
                                  uiLoggerNamesVendorPortal.UI_SST_EP_BANNER_REQUEST_CALLBACK_CLICK.functionName,
                              }
                            );
                            history.push("/vendorPortal/early-payment");
                          }}
                        >
                          Request a Callback
                        </Button>
                        <Button
                          variant="contained"
                          endIcon={<ChevronRight />}
                          sx={{
                            bgcolor: "#f9b200",
                            color: "#000",
                            "&:hover": { bgcolor: "#e6a500" },
                            fontWeight: 600,
                            fontSize: 14,
                            letterSpacing: "0.014px",
                            px: 2,
                            py: 0.5,
                            minHeight: 40,
                            minWidth: 78,
                            borderRadius: "4px",
                            boxShadow: "none",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            gap: 1,
                          }}
                          onClick={() => {
                            uiLogsVendorPortal(uiLoggerNamesVendorPortal.UI_SST_EP_BANNER_VIEW_OFFER_CLICK.message, {
                              function: uiLoggerNamesVendorPortal.UI_SST_EP_BANNER_VIEW_OFFER_CLICK.functionName,
                            });
                            history.push("/vendorPortal/early-payment");
                          }}
                        >
                          View Offer
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          {/* Render the children components */}
          <Box>{!embedMode && !location.pathname.includes("/faq") && <AlertsComponent />}</Box>
          {children}
        </Box>
      </Box>
    </VendorPortalContext.Provider>
  );
};

export default VendorPortalLayout;
